import React, { useMemo } from 'react';
import { Search } from 'lucide-react';
import { CONSTANTS } from '../../data.js';
import { useInventoryContext, useUIContext, useZoomContext, useWorkbenchContext, useDragOverlayContext } from '../../app/GameProviders';
import { InventoryItem } from './InventoryItem';
import { useInventorySearch } from './useInventorySearch';
import { SidebarResizer } from '../../ui/SidebarResizer';
import { useInventoryDrag } from '../../hooks/useInventoryDrag';
import type { DragInfo } from '../../types';
import { isPointInRect, screenToWorldCentered } from '../workbench/placement';

export function InventoryPanel() {
  const { inventoryPanelRef, inventoryWidth, searchQuery, setSearchQuery } = useUIContext();
  const { filteredItems } = useInventorySearch();
  const { scrollRef, zoom } = useZoomContext();
  const { addInstance } = useWorkbenchContext();
  const { isHoveringInventory } = useDragOverlayContext();

  const onDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: DragInfo, itemId: string) => {
    const sc = scrollRef.current;
    if (!sc) return;
    const containerRect = sc.getBoundingClientRect();
    if (isPointInRect(info.point, containerRect)) {
      const { x, y } = screenToWorldCentered(
        info.point,
        containerRect,
        sc,
        zoom,
        CONSTANTS.WORKBENCH_ITEM_SIZE
      );
      addInstance(itemId, x, y);
    }
  };

  const { startInventoryCopyDrag } = useInventoryDrag({ onDragEnd });

  const gridStyle = useMemo(() => ({
    gridTemplateColumns: `repeat(auto-fill, minmax(${CONSTANTS.INVENTORY_GRID_MIN_COL_WIDTH}px, 1fr))`
  }), []);

  return (
    <div
      ref={inventoryPanelRef}
      className="absolute left-0 top-16 bottom-4 z-40"
      style={{ width: `${inventoryWidth}px` }}
    >
      <div 
        className={`relative backdrop-blur rounded-xl p-4 h-full flex flex-col border transition-all duration-200 ${
          isHoveringInventory 
            ? `border-2 border-red-500 shadow-lg shadow-red-500/20`
            : 'bg-slate-800/50 border-slate-700/60'
        }`}
        style={{
          backgroundColor: isHoveringInventory 
            ? `rgba(${parseInt(CONSTANTS.DRAG_FEEDBACK.INVENTORY_DELETE_HIGHLIGHT_COLOR.slice(1, 3), 16)}, ${parseInt(CONSTANTS.DRAG_FEEDBACK.INVENTORY_DELETE_HIGHLIGHT_COLOR.slice(3, 5), 16)}, ${parseInt(CONSTANTS.DRAG_FEEDBACK.INVENTORY_DELETE_HIGHLIGHT_COLOR.slice(5, 7), 16)}, ${CONSTANTS.DRAG_FEEDBACK.INVENTORY_DELETE_HIGHLIGHT_OPACITY})`
            : undefined
        }}
      >
        <div className="flex-grow overflow-y-auto pr-2">
          <div className="grid gap-2" style={gridStyle}>
            {filteredItems.map(item => (
              <InventoryItem key={item.id} item={item} onPointerDown={startInventoryCopyDrag} />
            ))}
          </div>
        </div>

        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search ingredients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-700/50 border border-slate-600 rounded-md pl-9 pr-3 py-2 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        <SidebarResizer />
      </div>
    </div>
  );
}
